import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { saas } from '../lib/saasApi';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { CheckCircle2, Sparkles, Zap, Building, Loader2, AlertCircle, ArrowRight, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { PageLayout } from '../components/Sidebar';

const PLAN_ICONS = {
  free_personal: Sparkles,
  pro_personal: Zap,
  team: Building,
  business: Building,
};

const METRIC_LABELS = {
  ai_tokens: 'AI Tokens',
  agent_runs: 'Agent Runs',
  artefacts_created: 'Artefacts',
  tasks_created: 'Tasks',
};

function fmt(n) {
  if (n == null) return '∞';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

function PlanTile({ plan, currentKey, onSelect, busy }) {
  const Icon = PLAN_ICONS[plan.plan_key] || Sparkles;
  const isCurrent = plan.plan_key === currentKey;
  const isFree = plan.plan_key === 'free_personal';
  const featured = plan.plan_key === 'pro_personal';
  return (
    <Card
      data-testid={`billing-plan-${plan.plan_key}`}
      className={`relative ${featured ? 'border-violet-500 dark:border-violet-400 shadow-lg' : ''}`}
    >
      {featured && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className="bg-violet-600 hover:bg-violet-600">Most popular</Badge>
        </div>
      )}
      <CardHeader>
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5 text-violet-500" />
          <CardTitle className="text-xl">{plan.name}</CardTitle>
        </div>
        <CardDescription className="min-h-[2.5rem]">{plan.description}</CardDescription>
        <div className="mt-2">
          <span className="text-4xl font-bold">${plan.monthly_price_usd}</span>
          {!isFree && <span className="text-sm text-slate-500 ml-1">/mo</span>}
        </div>
      </CardHeader>
      <CardContent>
        {isCurrent ? (
          <Button data-testid={`billing-current-${plan.plan_key}`} disabled className="w-full" variant="outline">
            <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-500" />
            Current plan
          </Button>
        ) : isFree ? (
          <Button disabled className="w-full" variant="outline">Free forever</Button>
        ) : (
          <Button
            data-testid={`billing-upgrade-${plan.plan_key}`}
            onClick={() => onSelect(plan.plan_key)}
            disabled={busy}
            className="w-full bg-violet-600 hover:bg-violet-700"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
            Upgrade
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function UsageRow({ counter }) {
  const used = counter.used || 0;
  const limit = counter.limit;
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const danger = pct >= 90;
  return (
    <div data-testid={`usage-row-${counter.metric_key}`} className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">{METRIC_LABELS[counter.metric_key] || counter.metric_key}</span>
        <span className={danger ? 'text-red-500 font-medium' : 'text-slate-500'}>
          {fmt(used)} / {fmt(limit)}
        </span>
      </div>
      <Progress value={pct} className={danger ? '[&>div]:bg-red-500' : '[&>div]:bg-violet-500'} />
    </div>
  );
}

export default function BillingPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyPlan, setBusyPlan] = useState(null);

  // Handle ?session_id= return from Stripe (success page)
  const sessionId = params.get('session_id');
  const cameFromCheckout = params.get('checkout') === 'success';

  const reload = async () => {
    setLoading(true);
    try {
      const [planList, plan] = await Promise.all([saas.listPlans(), saas.myPlan()]);
      setPlans(planList.items || []);
      setMe(plan);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load billing data: ' + (e.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  // Poll checkout status if returning from Stripe
  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const status = await saas.checkoutStatus(sessionId);
        if (status.payment_status === 'paid' || status.already_processed) {
          toast.success('Payment successful — your plan has been upgraded');
          navigate('/billing', { replace: true });
          reload();
          return;
        }
        if (status.status === 'expired') {
          toast.error('Checkout session expired. Please try again.');
          navigate('/billing', { replace: true });
          return;
        }
        if (attempts < 6) {
          timer = setTimeout(poll, 2000);
        } else {
          toast.warning('Payment is still processing — refresh in a moment');
        }
      } catch (e) {
        console.error(e);
      }
    };
    poll();
    return () => timer && clearTimeout(timer);
  }, [sessionId]);  // eslint-disable-line

  const handleUpgrade = async (planKey) => {
    setBusyPlan(planKey);
    try {
      const result = await saas.createCheckout(planKey);
      if (result.url) {
        window.location.href = result.url;
      } else {
        toast.error('Could not start checkout');
      }
    } catch (e) {
      console.error(e);
      toast.error('Checkout failed: ' + (e.detail || e.message));
    } finally {
      setBusyPlan(null);
    }
  };

  return (
    <PageLayout>
      <div data-testid="billing-page" className="max-w-6xl mx-auto p-6 space-y-8">
        <div>
          <h1 className="text-3xl font-bold">Billing & Plan</h1>
          <p className="text-slate-500 mt-1">
            Choose a plan that fits how you work. Upgrade anytime — your usage carries over.
          </p>
        </div>

        {cameFromCheckout && (
          <Card className="border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/30">
            <CardContent className="pt-6 flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              <span>Payment confirmed — your plan was upgraded.</span>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
          </div>
        ) : (
          <>
            {/* Current Plan + Usage */}
            <Card data-testid="billing-current-plan">
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <div>
                  <CardTitle>Your plan</CardTitle>
                  <CardDescription>
                    Current: <Badge variant="outline" className="ml-1">{me?.plan?.name || me?.plan_key}</Badge>
                  </CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={reload}>
                  <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                </Button>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {(me?.usage || []).map((c) => (
                    <UsageRow key={c.metric_key} counter={c} />
                  ))}
                </div>
                {me?.subscription && me.subscription.provider === 'stripe' && (
                  <div className="text-xs text-slate-500 pt-2 border-t flex items-center justify-between gap-2 flex-wrap">
                    <span>
                      Stripe subscription: {me.subscription.stripe_subscription_id || me.subscription.stripe_session_id || '—'} ·
                      Status: {me.subscription.status}
                    </span>
                    <Button
                      data-testid="billing-portal-btn"
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          const r = await saas.openPortal();
                          if (r.url) window.location.href = r.url;
                        } catch (e) {
                          toast.error('Could not open billing portal: ' + (e.detail || e.message));
                        }
                      }}
                    >
                      Manage subscription
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Plan Tiles */}
            <div>
              <h2 className="text-xl font-semibold mb-4">Plans</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {plans.map((p) => (
                  <PlanTile
                    key={p.plan_key}
                    plan={p}
                    currentKey={me?.plan_key}
                    onSelect={handleUpgrade}
                    busy={busyPlan === p.plan_key}
                  />
                ))}
              </div>
            </div>

            {/* Entitlements snapshot */}
            <Card>
              <CardHeader>
                <CardTitle>Module access</CardTitle>
                <CardDescription>What's unlocked on your current plan</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {(me?.entitlements || []).filter(e => e.module_key).map((e) => {
                    const state = e.entitlement_state;
                    const badgeClass = state === 'included' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                      : state === 'limited' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                      : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400';
                    return (
                      <div key={e.module_key} data-testid={`entitlement-${e.module_key}`} className="flex items-center justify-between p-2 rounded-md border">
                        <span className="capitalize">{e.module_key.replace(/_/g, ' ')}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${badgeClass}`}>{state}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </PageLayout>
  );
}
